import { useMemo } from 'react';
import {
  UIMatch,
  useLocation,
  useMatches,
  useNavigate,
} from 'react-router-dom';
import { ArrowLeftIcon, cn, Logo } from '@quickmd-llc/design-system';
import { Events, getPageNameFromPath, tracking } from '@lib';
import ProgressBar from './ProgressBar';
import { POST_ONBOARDING_ROUTES } from '@constants';
import { useBuildPatientAppUrl } from 'hooks';
import { useIsLoggedIn } from '@hooks/useIsLoggedIn';
import { useRecalculateChatWidgetPositionOnMounAndUnmount } from '@components/LazyChatWidget/hooks/useRecalculateChatWidgetPositionOnMounAndUnmount';

interface CustomUIMatch extends UIMatch {
  handle:
    | {
        position?: number;
      }
    | undefined;
}
export const NAVIGATION_LOGO_IMG_TAG_ID = 'navigation-logo-img-tag';

interface Props {
  onBackClick?: () => void;
  hideBackButton?: boolean;
  hideProgressBar?: boolean;
  navigationSteps?: number;
  currentPosition?: number;
}

export default function Navigation({
  onBackClick,
  hideBackButton,
  hideProgressBar,
  navigationSteps = Object.keys(POST_ONBOARDING_ROUTES).length,
  currentPosition,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const { isLoggedIn } = useIsLoggedIn();
  const url = useBuildPatientAppUrl({ page: 'onboarding' });
  const { pathname } = useLocation();
  const matches = useMatches() as CustomUIMatch[];
  const position = useMemo(() => {
    const match = matches[0];
    return (currentPosition || match?.handle?.position) ?? 0;
  }, [matches, currentPosition]);

  function handleLogoClick() {
    if (window.ReactNativeWebView)
      window.ReactNativeWebView.postMessage('exit');
    else window.location.href = url;
  }
  function handleClick() {
    const page = getPageNameFromPath(pathname);

    tracking.trackEvent(Events.GO_BACK, {
      page,
    });

    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  }

  useRecalculateChatWidgetPositionOnMounAndUnmount();

  return (
    <div>
      {!hideProgressBar && (
        <div className="mb-4 flex h-8 items-center gap-2">
          <ProgressBar position={position} navigationSteps={navigationSteps} />
        </div>
      )}

      <div className="relative flex w-full flex-1 items-center justify-center">
        {!hideBackButton && (
          <button
            aria-label="Go back"
            className="absolute left-0 mb-auto mt-auto p-2 text-text-dark"
            onClick={handleClick}
          >
            <ArrowLeftIcon />
          </button>
        )}

        <Logo
          id={NAVIGATION_LOGO_IMG_TAG_ID}
          className={cn('cursor-pointer', {
            'pointer-events-none': !isLoggedIn,
          })}
          variant="full"
          onClick={handleLogoClick}
        />
      </div>
    </div>
  );
}
